import { useState } from 'react';
import { standardReportsService } from '@/services/standardReports.service';
import { getErrorMessage } from '@/services/apiClient';
import { DATE_RANGE_PRESETS } from '@/lib/dateRangePresets';
import type {
  StandardReportOrderInput,
  StandardReportParams,
} from '@/types/standardReports.types';

/**
 * Turns a report into a recurring delivery.
 *
 * This is the feature's reason for existing: the reports themselves can be run
 * on screen, but what people actually need is the same file arriving every
 * month without anyone remembering to produce it.
 *
 * The period is submitted as a PRESET KEY. The server re-resolves it on each
 * fire, so a monthly order covers the month that just closed every time.
 */

interface Props {
  reportKey: string;
  reportTitle: string;
  /** Pre-selected from whatever the user last ran, so the dialog agrees with the screen. */
  initialPreset: string;
  /** Non-period filters the order must keep, already shaped as run params. */
  filters?: Omit<StandardReportParams, 'datePreset' | 'dateFrom' | 'dateTo'>;
  onClose: () => void;
  onSaved: () => void;
}

const CADENCES: Array<{ unit: 'day' | 'week' | 'month' | 'year'; label: string }> = [
  { unit: 'day', label: 'Daily' },
  { unit: 'week', label: 'Weekly' },
  { unit: 'month', label: 'Monthly' },
  { unit: 'year', label: 'Yearly' },
];

export function ScheduleStandardReportDialog({
  reportKey,
  reportTitle,
  initialPreset,
  filters,
  onClose,
  onSaved,
}: Props) {
  const [title, setTitle] = useState(reportTitle);
  const [preset, setPreset] = useState(initialPreset);
  const [cadenceUnit, setCadenceUnit] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [cadenceCount, setCadenceCount] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState('06:00');
  const [recipients, setRecipients] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set only after the server has told us which addresses are external. Asking
  // up front would be a checkbox nobody reads; asking in response to named
  // addresses is a question with an answer.
  const [externalNames, setExternalNames] = useState<string[] | null>(null);

  const save = async (confirmExternal: boolean) => {
    setSaving(true);
    setError(null);

    const input: StandardReportOrderInput = {
      title: title.trim() || reportTitle,
      reportKey,
      params: { ...filters, datePreset: preset },
      recipients: recipients.trim(),
      recurring: true,
      cadenceUnit,
      cadenceCount,
      timeOfDay: `${timeOfDay}:00`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(confirmExternal ? { confirmExternalRecipients: true } : {}),
    };

    try {
      await standardReportsService.createOrder(input);
      onSaved();
    } catch (e) {
      const message = getErrorMessage(e, 'Could not save this schedule.');

      // The server names the offending addresses, so the confirmation can too.
      const names = extractExternalRecipients(e);

      if (names) {
        setExternalNames(names);
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-base font-semibold text-slate-900">Schedule this report</h2>
        </div>

        <div className="space-y-4 px-5 py-4">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Name</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Period each delivery covers
            </span>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            >
              {DATE_RANGE_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              Re-calculated on every delivery, so each file covers the period that just closed.
            </span>
          </label>

          <div className="flex gap-3">
            <label className="flex-1">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                How often
              </span>
              <select
                value={cadenceUnit}
                onChange={(e) => setCadenceUnit(e.target.value as typeof cadenceUnit)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
              >
                {CADENCES.map((c) => (
                  <option key={c.unit} value={c.unit}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="w-24">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Every
              </span>
              <input
                type="number"
                min={1}
                value={cadenceCount}
                onChange={(e) => setCadenceCount(Math.max(1, Number(e.target.value)))}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
              />
            </label>

            <label className="w-32">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                At
              </span>
              <input
                type="time"
                value={timeOfDay}
                onChange={(e) => setTimeOfDay(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Email to (optional)
            </span>
            <input
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="someone@example.com, someone.else@example.com"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            />
            {/* Says what is sent, because "emailed a report" reasonably reads as
                "emailed a spreadsheet" and it is not. */}
            <span className="mt-1 block text-xs text-slate-500">
              Recipients receive an expiring download link, never the file itself.
            </span>
          </label>

          {externalNames && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p className="font-medium">These addresses are outside your practice:</p>
              <p className="mt-1 break-words">{externalNames.join(', ')}</p>
              <p className="mt-2">
                Reports can contain patient information. Send to them anyway?
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-slate-300 px-4 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save(externalNames !== null)}
            disabled={saving}
            className="rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving
              ? 'Saving…'
              : externalNames
                ? 'Send to them anyway'
                : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Pulls the named external addresses out of the server's refusal, so the
 * confirmation can list them instead of asking about "some recipients".
 */
function extractExternalRecipients(error: unknown): string[] | null {
  const response = (error as { response?: { data?: { error?: { code?: string; details?: unknown } } } })
    ?.response?.data?.error;

  if (response?.code !== 'EXTERNAL_RECIPIENTS_UNCONFIRMED') return null;

  const details = response.details as { recipients?: unknown } | undefined;
  const list = details?.recipients;

  return Array.isArray(list) ? list.map(String) : [];
}
